import React, { useState } from 'react';
import { User, ContractorInvoice } from '../types';
import { Icons } from '../constants';

interface Props {
  user: User;
  contractorInvoices: ContractorInvoice[];
  onAddContractorInvoice: (invoice: ContractorInvoice) => void;
  onUpdateContractorInvoice: (invoice: ContractorInvoice) => void;
}

const ContractorFinanceView: React.FC<Props> = ({ user, contractorInvoices, onAddContractorInvoice, onUpdateContractorInvoice }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock data for contractor
  const currentContract = "1099 Independent Contractor Agreement";
  const rate = "$65.00 / hr";
  
  const myInvoices = contractorInvoices.filter(i => i.contractorId === user.id);
  const pendingInvoices = myInvoices.filter(i => i.status === 'PENDING' || i.status === 'APPROVED').reduce((sum, i) => sum + i.amount, 0) + 2730.00;
  const ytdEarnings = myInvoices.filter(i => i.status === 'PAID').reduce((sum, i) => sum + i.amount, 0) + 12450.00;
  const hoursBilled = Math.round(pendingInvoices / 65);

  const handleSubmitInvoice = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount))) return;
    
    const newInvoice: ContractorInvoice = {
      id: `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`,
      contractorId: user.id,
      contractorName: user.name,
      amount: Number(amount),
      date: new Date().toISOString(),
      status: 'PENDING',
      description,
      invoiceUrl
    };
    
    onAddContractorInvoice(newInvoice);
    setAmount('');
    setDescription('');
    setInvoiceUrl(undefined);
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl mx-auto">
      <div className="bg-bark p-10 rounded-[40px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-xl">
        <div className="relative z-10">
          <h2 className="text-3xl font-black mb-2">Contractor Portal</h2>
          <p className="text-stone-400 font-medium text-lg">Manage your invoices, contracts, and payments.</p>
        </div>
        <div className="absolute right-0 top-0 opacity-5 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none text-gold">
           <Icons.Finance />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Active Contract</p>
          <h3 className="text-xl font-black text-bark mt-2">{currentContract}</h3>
          <p className="text-sm text-stone-500 mt-1">Rate: {rate}</p>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Pending Invoices</p>
          <h3 className="text-4xl font-black text-orange-500 mt-2">${pendingInvoices.toFixed(2)}</h3>
          <p className="text-sm text-stone-500 mt-1">~{hoursBilled} hrs billed</p>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">YTD Earnings</p>
          <h3 className="text-4xl font-black text-gecko mt-2">${ytdEarnings.toFixed(2)}</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-black text-bark">Recent Invoices</h3>
            <button 
              onClick={() => setIsSubmitting(!isSubmitting)}
              className="px-4 py-2 bg-palm text-white rounded-xl font-bold text-sm hover:bg-gecko transition"
            >
              {isSubmitting ? 'Cancel' : '+ Submit Invoice'}
            </button>
          </div>
          
          {isSubmitting && (
            <form onSubmit={handleSubmitInvoice} className="mb-6 space-y-4 p-4 bg-stone-50 rounded-2xl border border-stone-200">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full p-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Tree removal at 123 Main St"
                  className="w-full p-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-1">Invoice Document (Optional)</label>
                {invoiceUrl ? (
                  <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-50 h-32 flex items-center justify-center">
                    <img src={invoiceUrl} alt="Invoice Preview" className="h-full object-contain" />
                    <button 
                      type="button"
                      onClick={() => setInvoiceUrl(undefined)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow-md hover:bg-red-600 transition"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-full p-4 bg-stone-50 rounded-xl border border-stone-200 border-dashed flex flex-col items-center justify-center text-center">
                    <input 
                      type="file" 
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        if (!e.target.files?.[0]) return;
                        const reader = new FileReader();
                        reader.onloadend = () => setInvoiceUrl(reader.result as string);
                        reader.readAsDataURL(e.target.files[0]);
                      }}
                      className="hidden"
                      id="contractor-invoice-upload"
                    />
                    <label 
                      htmlFor="contractor-invoice-upload"
                      className="cursor-pointer text-gecko font-bold hover:text-palm transition"
                    >
                      Click to browse
                    </label>
                  </div>
                )}
              </div>
              <button type="submit" className="w-full py-3 bg-bark text-white rounded-xl font-bold hover:bg-stone-800 transition">
                Submit Invoice
              </button>
            </form>
          )}

          <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
            {myInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-stone-50 rounded-2xl border border-stone-100">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-stone-300 mb-3 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </div>
                <p className="text-stone-500 font-medium text-sm">No recent invoices.</p>
                <p className="text-stone-400 text-xs mt-1">Submit an invoice above to see it here.</p>
              </div>
            ) : (
              myInvoices.map(invoice => (
                <div key={invoice.id} className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl border border-stone-200">
                  <div>
                    <span className="font-bold text-bark block">{invoice.id}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-stone-500">Submitted {invoice.date}</span>
                      {invoice.invoiceUrl && (
                        <a 
                          href={invoice.invoiceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[10px] text-palm font-bold hover:underline flex items-center"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
                          View
                        </a>
                      )}
                    </div>
                    <span className="text-xs text-stone-400 block mt-1">{invoice.description}</span>
                  </div>
                  <div className="text-right">
                    <span className={`font-black block ${(invoice.status === 'PAID' || invoice.status === 'APPROVED') ? 'text-gecko' : invoice.status === 'REJECTED' ? 'text-red-500' : 'text-orange-500'}`}>
                      ${invoice.amount.toFixed(2)}
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${(invoice.status === 'PAID' || invoice.status === 'APPROVED') ? 'text-gecko' : invoice.status === 'REJECTED' ? 'text-red-500' : 'text-orange-400'}`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Tax Documents</h3>
          <div className="space-y-4">
            <button className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition rounded-2xl border border-stone-200 group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-stone-400 shadow-sm group-hover:text-palm transition">
                  📄
                </div>
                <div className="text-left">
                  <p className="font-bold text-bark">2025 1099-NEC</p>
                  <p className="text-xs text-stone-500">Available Jan 31, 2026</p>
                </div>
              </div>
              <span className="text-palm font-bold text-sm">Download</span>
            </button>
            <button className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition rounded-2xl border border-stone-200 group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-stone-400 shadow-sm group-hover:text-palm transition">
                  📄
                </div>
                <div className="text-left">
                  <p className="font-bold text-bark">Current W-9</p>
                  <p className="text-xs text-stone-500">Last updated: Jan 15, 2026</p>
                </div>
              </div>
              <span className="text-palm font-bold text-sm">Update</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorFinanceView;